import { RequestStatus } from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { LoadingSpinnerPresets } from 'dg-web-shared/common/components/material-ui/PresetLoadingSpinner.tsx';
import {
    Button,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { AutoMode, Download, Percent, QrCode2 } from '@mui/icons-material';
import { currencyCentsToLocalPrice } from 'dg-web-shared/lib/NumberFormatter.ts';
import {
    EmptyResults,
    ResultLimitAlert,
} from '../shared-mui-components/ListMessages';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection';
import { Localized, useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import { MainContent } from '../shared-mui-components/MainContent.tsx';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { HeaderWithButtons } from '../shared-mui-components/HeaderWithButtons.tsx';
import { getNavigationText } from '../layout/components/BreadCrumb.tsx';
import { OperatorAppRoutes } from '../app/config/OperatorRoutingConfig.tsx';
import { Message } from 'dg-web-shared/lib/Localized.ts';
import { Language } from 'dg-web-shared/lib/Text.ts';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';
import { FilterBar } from '../shared-mui-components/filter/FiltersBox.tsx';
import { useRecodeStatementsFilterConfiguration } from '../shared-mui-components/filter/OperatorFilterConfiguration.tsx';
import { FiltersDropdown } from '../parkingabo-users/OperatorParkingaboUsersFilterList.tsx';
import {
    setSearchURL,
    useQuery,
} from '../shared-mui-components/filter/OperatorFilterHelpers.tsx';
import { urlWithOperatorExternalId } from '../utils/Utils.ts';

export interface RecodeStatementsResponse {
    data: RecodeStatement[];
    totalCount: number;
    totalAmountRappen: number;
    eof: boolean;
}

interface RecodeStatement {
    id: number;
    yearMonth: string;
    description: Message;
    recodeCount: number;
    recodeAmountRappen: number;
    hasDownload: boolean;
}

export function CloudConnectorRecodeStatements() {
    const { language } = useLanguage();
    const { operatorData, currentLogin } = useOperatorContext();
    const navigate = useNavigate();
    const location = useLocation();
    const {
        filterConfig,
        activeFilters,
        listState,
        filterConfigState,
        searchText,
    } = useRecodeStatementsFilterConfiguration();

    return (
        <MainContent>
            <HeaderWithButtons
                title={getNavigationText(
                    OperatorAppRoutes.CloudConnectorRecodeStatements,
                )}
                icon={Percent}
                headerButtons={
                    <Stack
                        direction="row"
                        spacing={3}
                        sx={{
                            alignItems: 'center',
                        }}
                    >
                        {currentLogin.permissions.cloudConnectorAutoRecode && (
                            <Link to={'../auto-recode'}>
                                <Button
                                    variant="outlined"
                                    startIcon={<AutoMode />}
                                    color="primary"
                                >
                                    <Localized
                                        de="Autom. Rabatt"
                                        fr="Rabais autom."
                                        it="Ribasso autom."
                                        en="Auto disc."
                                    />
                                </Button>
                            </Link>
                        )}
                        {operatorData.hasCloudConnectorRecodeQrCodes && (
                            <Link to={'../qr-codes'}>
                                <Button
                                    variant="outlined"
                                    startIcon={<QrCode2 />}
                                    color="primary"
                                    sx={{ paddingLeft: '15px' }}
                                >
                                    <Localized
                                        de="Rabattkarten"
                                        fr="Cartes de rabais"
                                        it="Carte ribasso"
                                        en="Discount cards"
                                    />
                                </Button>
                            </Link>
                        )}
                    </Stack>
                }
            />
            <FilterBar>
                <FiltersDropdown
                    activeFilters={activeFilters}
                    filterConfig={filterConfig}
                    onFiltersStateChange={activeFilters =>
                        setSearchURL(
                            navigate,
                            location,
                            searchText,
                            activeFilters,
                        )
                    }
                    pending={
                        filterConfigState.status === RequestStatus.PENDING &&
                        Object.keys(filterConfig).length === 0
                    }
                />
            </FilterBar>
            <OperatorAsyncLoadedSection
                requestState={listState}
                pendingLoaderPreset={
                    LoadingSpinnerPresets.FillAllSpaceAndCenter
                }
                render={(recodeStatements: RecodeStatementsResponse) => {
                    if (recodeStatements.data.length === 0) {
                        return <EmptyResults />;
                    }
                    return (
                        <TableContainer>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <Localized
                                                de="Monat"
                                                fr="Mois"
                                                it="Mese"
                                                en="Month"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Localized
                                                de="Bedingung"
                                                fr="Condition"
                                                it="Condizione"
                                                en="Condition"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Localized
                                                de="Anzahl"
                                                fr="Quantité"
                                                it="Quantità"
                                                en="Quantity"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Localized
                                                de="Gesamtrabatt (CHF)"
                                                fr="Remise totale (CHF)"
                                                it="Sconto totale (CHF)"
                                                en="Total discount (CHF)"
                                            />
                                        </TableCell>
                                        <TableCell>EXCEL</TableCell>
                                    </TableRow>
                                </TableHead>
                                <RecodeStatemenSummaryRow
                                    hasTotalDownload={recodeStatements.data.some(
                                        d => d.hasDownload,
                                    )}
                                    totalCount={recodeStatements.totalCount}
                                    totalAmountRappen={
                                        recodeStatements.totalAmountRappen
                                    }
                                    language={language}
                                />
                                <TableBody>
                                    {recodeStatements.data.map(
                                        recodeStatement => (
                                            <RecodeStatementRow
                                                key={recodeStatement.id}
                                                recodeStatement={
                                                    recodeStatement
                                                }
                                                language={language}
                                            />
                                        ),
                                    )}
                                </TableBody>
                            </Table>
                            <ResultLimitAlert
                                eof={recodeStatements.eof}
                                numberOfResults={recodeStatements.data.length}
                                hasExport={false}
                            />
                        </TableContainer>
                    );
                }}
            />
        </MainContent>
    );
}

function RecodeStatemenSummaryRow({
    hasTotalDownload,
    totalCount,
    totalAmountRappen,
    language,
}: {
    hasTotalDownload: boolean;
    totalCount: number;
    totalAmountRappen: number;
    language: Language;
}) {
    const { operatorExternalId } = useOperatorContext();
    const query = useQuery();
    const params = new URLSearchParams(query);
    params.set('lang', language);
    return (
        <TableRow
            sx={{
                color: theme => theme.palette.primary.contrastText,
                backgroundColor: theme => theme.palette.primary.main,
            }}
        >
            <TableCell sx={{ color: 'inherit' }}>
                <Localized de="Gesamt" fr="Totale" it="Totale" en="Total" />
            </TableCell>
            <TableCell />
            <TableCell sx={{ color: 'inherit' }}>{totalCount}</TableCell>
            <TableCell sx={{ color: 'inherit' }}>
                {currencyCentsToLocalPrice(totalAmountRappen)}
            </TableCell>
            <TableCell>
                {hasTotalDownload && (
                    <a
                        target="_blank"
                        href={urlWithOperatorExternalId(
                            `/ui-api/operator-account/cloud-connector/recode/filtered-statements/export?${params.toString()}`,
                            operatorExternalId,
                        )}
                        rel="noreferrer"
                    >
                        <Download
                            sx={{
                                color: theme =>
                                    theme.palette.primary.contrastText,
                                '&:hover': {
                                    color: theme => theme.palette.blue.electric,
                                },
                            }}
                        />
                    </a>
                )}
            </TableCell>
        </TableRow>
    );
}

function RecodeStatementRow({
    recodeStatement,
    language,
}: {
    recodeStatement: RecodeStatement;
    language: Language;
}) {
    const { operatorExternalId } = useOperatorContext();
    return (
        <TableRow>
            <TableCell>{recodeStatement.yearMonth}</TableCell>
            <TableCell>
                <Localized {...recodeStatement.description} />
            </TableCell>
            <TableCell>{recodeStatement.recodeCount}</TableCell>
            <TableCell>
                {currencyCentsToLocalPrice(recodeStatement.recodeAmountRappen)}
            </TableCell>
            <TableCell>
                {recodeStatement.hasDownload && (
                    <a
                        target="_blank"
                        href={urlWithOperatorExternalId(
                            `/ui-api/operator-account/cloud-connector/recode/statement/${recodeStatement.id}/export?lang=${language}`,
                            operatorExternalId,
                        )}
                        rel="noreferrer"
                    >
                        <Download
                            sx={{
                                color: theme => theme.palette.primary.main,
                                '&:hover': {
                                    color: theme => theme.palette.blue.electric,
                                },
                            }}
                        />
                    </a>
                )}
            </TableCell>
        </TableRow>
    );
}
